import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Users, Headphones } from 'lucide-react'
import { Badge } from '#app/components/ui/badge'
import { Button } from '#app/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '#app/components/ui/card'
import { Input } from '#app/components/ui/input'
import { Label } from '#app/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#app/components/ui/select'
import { Textarea } from '#app/components/ui/textarea'
import { type Route } from './+types/contact'

export const meta: Route.MetaFunction = () => [
  { title: 'Contact - Woonplan' },
  { name: 'description', content: 'Neem contact op met ons team voor vragen over je renovatieproject' }
]

const contactMethods = [
  {
    icon: MessageSquare,
    title: 'Chat met ons',
    description: 'Directe hulp via live chat',
    action: 'Start chat',
    available: true,
    timing: 'Direct antwoord'
  },
  {
    icon: Mail,
    title: 'Email support',
    description: 'Stuur ons je vraag',
    action: 'hello@woonplan.nl',
    available: true,
    timing: 'Antwoord binnen 24 uur'
  },
  {
    icon: Phone,
    title: 'Bel ons',
    description: 'Ma-Vr 9:00-17:00',
    action: '+31 20 123 4567',
    available: true,
    timing: 'Directe hulp'
  },
  {
    icon: Headphones,
    title: 'Support center',
    description: 'Zoek antwoorden',
    action: 'Bekijk FAQ',
    available: true,
    timing: 'Self-service'
  }
]

const topics = [
  'Algemene vraag',
  'Technische ondersteuning',
  'Facturatie & betalingen',
  'Partnership aanvraag',
  'Enterprise oplossing',
  'Feedback & suggesties',
  'Media & pers'
]

const offices = [
  {
    city: 'Amsterdam',
    address: 'Singel 542',
    postal: '1017 AZ Amsterdam',
    country: 'Nederland',
    primary: true
  },
  {
    city: 'Utrecht',
    address: 'Vechtclub XL, Europalaan 2b',
    postal: '3526 KS Utrecht',
    country: 'Nederland',
    primary: false
  }
]

export default function ContactPage() {
  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="container max-w-6xl mx-auto px-4 text-center mb-16">
        <Badge className="mb-4" variant="secondary">
          <Users className="h-3 w-3 mr-1" />
          Contact
        </Badge>
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          We staan voor je klaar
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Of je nu een vraag hebt over je renovatie, technische hulp nodig hebt, 
          of feedback wilt delen - we horen graag van je.
        </p>
      </section>

      {/* Contact Methods */}
      <section className="container max-w-6xl mx-auto px-4 mb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactMethods.map((method, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-2">
                  <method.icon className="h-8 w-8 text-primary" />
                  {method.available && (
                    <Badge variant="secondary" className="text-xs">
                      Beschikbaar
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-lg">{method.title}</CardTitle>
                <CardDescription className="text-sm">
                  {method.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="font-medium text-sm mb-1">{method.action}</p>
                <p className="text-xs text-muted-foreground">{method.timing}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Main Contact Form */}
      <section className="container max-w-6xl mx-auto px-4 mb-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Stuur ons een bericht</CardTitle>
                <CardDescription>
                  Vul het formulier in en we nemen zo snel mogelijk contact met je op.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Voornaam *</Label>
                      <Input 
                        id="firstName" 
                        placeholder="Jan"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Achternaam *</Label>
                      <Input 
                        id="lastName" 
                        placeholder="Jansen"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="jan@example.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefoon</Label>
                      <Input 
                        id="phone" 
                        type="tel" 
                        placeholder="+31 6 12345678"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="topic">Onderwerp *</Label>
                    <Select required>
                      <SelectTrigger id="topic">
                        <SelectValue placeholder="Kies een onderwerp" />
                      </SelectTrigger>
                      <SelectContent>
                        {topics.map((topic) => (
                          <SelectItem key={topic} value={topic.toLowerCase().replace(/ /g, '-')}>
                            {topic}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Bedrijf (optioneel)</Label>
                    <Input 
                      id="company" 
                      placeholder="Naam van je bedrijf"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Bericht *</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Vertel ons meer over je vraag..."
                      className="min-h-[150px]"
                      required
                    />
                  </div>

                  <div className="flex items-start gap-2">
                    <input 
                      type="checkbox" 
                      id="privacy" 
                      className="mt-1"
                      required
                    />
                    <Label htmlFor="privacy" className="text-sm text-muted-foreground">
                      Ik ga akkoord met het verwerken van mijn gegevens volgens het{' '}
                      <a href="/privacy" className="underline">privacybeleid</a>
                    </Label>
                  </div>

                  <Button type="submit" size="lg" className="w-full">
                    Verstuur bericht
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Info */}
          <div className="space-y-6">
            {/* Office Locations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Onze kantoren</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {offices.map((office, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{office.city}</span>
                      {office.primary && (
                        <Badge variant="secondary" className="text-xs">
                          Hoofdkantoor
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground pl-6">
                      {office.address}<br />
                      {office.postal}<br />
                      {office.country}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Business Hours */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Openingstijden</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Maandag - Vrijdag</span>
                    <span className="font-medium">9:00 - 17:00</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Zaterdag</span>
                    <span className="font-medium">10:00 - 14:00</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Zondag</span>
                    <span className="font-medium">Gesloten</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Tijdzone: CET (Amsterdam)</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Help */}
            <Card className="bg-primary text-primary-foreground">
              <CardHeader>
                <CardTitle className="text-lg">Directe hulp nodig?</CardTitle>
                <CardDescription className="text-primary-foreground/80">
                  Voor dringende vragen kun je direct contact opnemen
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="secondary" 
                  className="w-full justify-start"
                  size="sm"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Start live chat
                </Button>
                <Button 
                  variant="secondary" 
                  className="w-full justify-start"
                  size="sm"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  +31 20 123 4567
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container max-w-4xl mx-auto px-4">
        <Card className="text-center p-8 md:p-12 bg-muted/50">
          <h2 className="text-2xl font-bold mb-4">
            Veelgestelde vragen
          </h2>
          <p className="text-muted-foreground mb-6">
            Vind snel antwoorden op de meest gestelde vragen in ons uitgebreide help center.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" size="lg">
              Bekijk FAQ's
            </Button>
            <Button variant="outline" size="lg">
              Help Center
            </Button>
          </div>
        </Card>
      </section>
    </div>
  )
}